import { NOME_DO_CANAL, type CanalDeAcesso } from '@orbind/core'
import { Button } from '@orbind/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { api, apiOrigin } from '../../lib/api.ts'
import { NOME_DO_SISTEMA, sistemaOperacional, urlDeDownload } from '../../lib/platform.ts'
import type { Bloqueio } from './canal-bloqueado.ts'
import { esquecerDispensaDoDesktop } from './convite-desktop.ts'
import './canal-bloqueado.css'

/**
 * Tela que toma o lugar do app quando o canal atual não está liberado
 * (SPEC-20261008-1104-canais-de-acesso).
 *
 * Pedido do Cristian (2026-10-08): quando o uso exigido é pelo desktop, ao
 * entrar pela web "disponibilizar o download e informa que o uso deve ser
 * pelo desktop sendo instalado o orbind na máquina". Por isso o caso do
 * desktop é o principal: explica, oferece o instalador do sistema da pessoa e
 * deixa sair. Os outros casos só dizem por onde a pessoa pode entrar.
 */

type Release = { platform: string; version: string; sizeBytes: number }

export function CanalBloqueado({
  bloqueio,
  atual,
  onEntrarMesmoAssim,
}: {
  bloqueio: NonNullable<Bloqueio>
  atual: CanalDeAcesso
  /** Só para o super admin: passa por cima da regra nesta aba. */
  onEntrarMesmoAssim?: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const sistema = sistemaOperacional()
  // no celular não há instalador a oferecer: o download é no computador
  const podeBaixarAqui = bloqueio.tipo === 'usar-desktop' && atual === 'navegador' && sistema !== 'outro'

  const [release, setRelease] = useState<Release | null | undefined>(undefined)
  useEffect(() => {
    if (!podeBaixarAqui) return
    let vivo = true
    fetch(`${apiOrigin()}/download/releases`)
      .then((r) => (r.ok ? (r.json() as Promise<{ releases: Release[] }>) : null))
      .then((d) => vivo && setRelease(d?.releases.find((r) => r.platform === sistema) ?? null))
      .catch(() => vivo && setRelease(null))
    return () => {
      vivo = false
    }
  }, [podeBaixarAqui, sistema])

  const sair = () => {
    esquecerDispensaDoDesktop()
    api.logout()
    queryClient.clear()
    navigate('/login', { replace: true })
  }

  const nomeDoSistema = NOME_DO_SISTEMA[sistema]
  const liberados = bloqueio.liberados.map((c) => NOME_DO_CANAL[c]).join(' · ')

  return (
    <main className="cbl">
      <section className="cbl__cartao" aria-labelledby="cbl-titulo">
        <div className="cbl__icone" aria-hidden>
          <i className={bloqueio.tipo === 'usar-desktop' ? 'fas fa-desktop' : 'fas fa-lock'} />
        </div>

        {bloqueio.tipo === 'usar-desktop' ? (
          <>
            <h1 id="cbl-titulo" className="cbl__titulo">
              Use o Orbind instalado no computador
            </h1>
            <p className="cbl__texto">
              Neste workspace, o Orbind é usado pelo <strong>aplicativo desktop</strong>, instalado na sua máquina —
              {atual === 'celular' ? ' o acesso pelo celular' : ' o acesso pelo navegador'} não está liberado para você.
              Instale o Orbind e entre com o mesmo e-mail e senha.
            </p>
            {atual === 'celular' ? (
              <p className="cbl__nota">
                <i className="fas fa-info-circle" aria-hidden />
                <span>Abra o Orbind no navegador do seu computador para baixar o instalador.</span>
              </p>
            ) : null}
          </>
        ) : (
          <>
            <h1 id="cbl-titulo" className="cbl__titulo">
              {atual === 'desktop'
                ? 'O acesso pelo desktop não está liberado'
                : atual === 'celular'
                  ? 'O acesso pelo celular não está liberado'
                  : 'O acesso pelo navegador não está liberado'}
            </h1>
            <p className="cbl__texto">
              Neste workspace, você pode usar o Orbind por: <strong>{liberados}</strong>. Se precisar de outro canal,
              fale com o responsável pelo workspace.
            </p>
          </>
        )}

        <div className="cbl__acoes">
          {podeBaixarAqui && release ? (
            <Button variant="primary" icon="fas fa-download" onClick={() => window.open(urlDeDownload(sistema), '_self')}>
              Baixar para {nomeDoSistema}
            </Button>
          ) : null}
          {podeBaixarAqui && release === null ? (
            <p className="cbl__nota">
              <i className="fas fa-info-circle" aria-hidden />
              <span>
                O instalador para {nomeDoSistema} ainda não está disponível. Veja as versões na{' '}
                <a href="/download">página de download</a>.
              </span>
            </p>
          ) : null}
          <Button variant="ghost" icon="fas fa-sign-out-alt" onClick={sair}>
            Sair
          </Button>
          {onEntrarMesmoAssim ? (
            <Button variant="ghost" icon="fas fa-user-shield" onClick={onEntrarMesmoAssim}>
              Entrar mesmo assim (super admin)
            </Button>
          ) : null}
        </div>

        {podeBaixarAqui && release ? (
          <p className="cbl__versao">
            Versão {release.version} · {(release.sizeBytes / 1024 / 1024).toFixed(1)} MB ·{' '}
            <a href="/download">outros sistemas</a>
          </p>
        ) : null}
      </section>
    </main>
  )
}
